import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { RequireAuth, RequirePermission } from './auth/guards.tsx';
import { AppShell } from './app/AppShell.tsx';
import { TableCards } from './lib/TableCards.tsx';
import { NotFound } from './app/NotFound.tsx';
import { AppError } from './app/AppError.tsx';
import { Login } from './pages/Login.tsx';
import { ClinicSignup } from './pages/ClinicSignup.tsx';
import { AudienceHome } from './app/AudienceHome.tsx';
import { CasesPage } from './pages/cases/CasesPage.tsx';
import { CaseDetailPage } from './pages/cases/CaseDetailPage.tsx';
import { ClinicsPage } from './pages/clinics/ClinicsPage.tsx';
import { PatientsPage } from './pages/patients/PatientsPage.tsx';
import { MediaReviewPage } from './pages/patients/MediaReviewPage.tsx';
import { ManufacturingPage } from './pages/manufacturing/ManufacturingPage.tsx';
import { PriceListPage } from './pages/finance/PriceListPage.tsx';
import { InvoicesPage } from './pages/finance/InvoicesPage.tsx';
// The books are a whole section most staff never open — lazily loaded so they cost nothing.
const Acct = () => import('./pages/finance/AccountingPages.tsx');
const AccountingLayout = lazy(() => Acct().then((m) => ({ default: m.AccountingLayout })));
const AccountingOverview = lazy(() => Acct().then((m) => ({ default: m.AccountingOverview })));
const JournalsPage = lazy(() => Acct().then((m) => ({ default: m.JournalsPage })));
const NewJournalPage = lazy(() => Acct().then((m) => ({ default: m.NewJournalPage })));
const NewExpensePage = lazy(() => Acct().then((m) => ({ default: m.NewExpensePage })));
const TrialBalancePage = lazy(() => Acct().then((m) => ({ default: m.TrialBalancePage })));
const ProfitLossPage = lazy(() => Acct().then((m) => ({ default: m.ProfitLossPage })));
const BalanceSheetPage = lazy(() => Acct().then((m) => ({ default: m.BalanceSheetPage })));
const ChartOfAccountsPage = lazy(() => Acct().then((m) => ({ default: m.ChartOfAccountsPage })));
const AccountLedgerPage = lazy(() => Acct().then((m) => ({ default: m.AccountLedgerPage })));
const PeriodsPage = lazy(() => Acct().then((m) => ({ default: m.PeriodsPage })));
const OpeningBalancesPage = lazy(() => Acct().then((m) => ({ default: m.OpeningBalancesPage })));
import { StaffInvoicePrintPage, PortalInvoicePrintPage } from './pages/finance/InvoicePrintPage.tsx';
import { PaymentsPage } from './pages/finance/PaymentsPage.tsx';
import { SettingsPage } from './pages/admin/SettingsPage.tsx';
import { UsersPage } from './pages/admin/UsersPage.tsx';
import { NotificationsPage } from './pages/admin/NotificationsPage.tsx';
import { RbacMatrix } from './pages/admin/RbacMatrix.tsx';
import { AuditLog } from './pages/admin/AuditLog.tsx';
import { LeadsPage } from './pages/admin/LeadsPage.tsx';
import { SecurityDashboard } from './pages/admin/SecurityDashboard.tsx';
import { WorkflowLayout } from './pages/workflow/WorkflowLayout.tsx';
import { WfDashboardPage } from './pages/workflow/WfDashboard.tsx';
import { WfBoardPage } from './pages/workflow/WfBoard.tsx';
import { WfCasesPage } from './pages/workflow/WfCases.tsx';
import { WfCaseDetailPage } from './pages/workflow/WfCaseDetail.tsx';
import { WfCalendarPage } from './pages/workflow/WfCalendar.tsx';
import { WfCompliancePage } from './pages/workflow/WfCompliance.tsx';
import { WfReportsPage } from './pages/workflow/WfReports.tsx';
import { WfAdminPage } from './pages/workflow/WfAdmin.tsx';
import { WfInboxPage } from './pages/workflow/WfInbox.tsx';
import { WfBillingPage } from './pages/workflow/WfBilling.tsx';
import { PortalLayout, PortalHome, PortalCases, PortalCaseDetail } from './pages/portal/PortalShell.tsx';
import { PortalChat } from './pages/portal/PortalChat.tsx';
import { ChatPage } from './pages/chat/ChatPage.tsx';
import { PortalNotices, PortalAssessment, PortalBookScan, PortalPreferences, PortalProfile } from './pages/portal/PortalPages.tsx';
import { PortalLedger, PortalPay, PortalInvoices } from './pages/portal/PortalBilling.tsx';
import { SubmitCaseWizard } from './pages/portal/SubmitCaseWizard.tsx';
const SharedSetupPage = lazy(() => import('./pages/SharedSetupPage.tsx').then((m) => ({ default: m.SharedSetupPage })));
// Leaflet only matters on the tracking screens, so the whole field module is split out — the
// same treatment CaseFilesPanel gives the three.js viewer.
const FieldLayout = lazy(() => import('./pages/field/FieldShell.tsx').then((m) => ({ default: m.FieldLayout })));
const FieldDuty = lazy(() => import('./pages/field/FieldDuty.tsx').then((m) => ({ default: m.FieldDuty })));
const FieldVisitForm = lazy(() => import('./pages/field/FieldVisitForm.tsx').then((m) => ({ default: m.FieldVisitForm })));
const FieldMyDay = lazy(() => import('./pages/field/FieldMyDay.tsx').then((m) => ({ default: m.FieldMyDay })));
const FieldToday = lazy(() => import('./pages/field/FieldToday.tsx').then((m) => ({ default: m.FieldToday })));
const FieldPlan = lazy(() => import('./pages/field/FieldPlan.tsx').then((m) => ({ default: m.FieldPlan })));
const FieldClinics = lazy(() => import('./pages/field/FieldClinics.tsx').then((m) => ({ default: m.FieldClinics })));
const WfScanPage = lazy(() => import('./pages/workflow/WfScan.tsx').then((m) => ({ default: m.WfScanPage })));
const ClinicCasesPage = lazy(() => import('./pages/clinics/ClinicCasesPage.tsx').then((m) => ({ default: m.ClinicCasesPage })));
const FieldFollowups = lazy(() => import('./pages/field/FieldPlan.tsx').then((m) => ({ default: m.FieldFollowups })));
const LabFieldTeam = lazy(() => import('./pages/field/LabFieldTeam.tsx').then((m) => ({ default: m.LabFieldTeam })));
const LabFieldTerritories = lazy(() => import('./pages/field/LabFieldTeam.tsx').then((m) => ({ default: m.LabFieldTerritories })));
const LabFieldLive = lazy(() => import('./pages/field/LabFieldLive.tsx').then((m) => ({ default: m.LabFieldLive })));
const LabFieldRepDay = lazy(() => import('./pages/field/LabFieldRepDay.tsx').then((m) => ({ default: m.LabFieldRepDay })));
const FieldReportPrintPage = lazy(() => import('./pages/field/FieldReportPrintPage.tsx').then((m) => ({ default: m.FieldReportPrintPage })));
const LabFieldPlaces = lazy(() => import('./pages/field/LabFieldPlaces.tsx').then((m) => ({ default: m.LabFieldPlaces })));
const LabFieldReports = lazy(() => import('./pages/field/LabFieldReports.tsx').then((m) => ({ default: m.LabFieldReports })));

/** Lazy route element with the chunk-loading fallback these screens share. */
const chunk = (node: React.ReactNode) => (
  <Suspense fallback={<div className="grid h-full place-items-center p-10 text-muted">Loading…</div>}>
    {node}
  </Suspense>
);

const router = createBrowserRouter([
  { path: '/login', element: <Login />, errorElement: <AppError /> },
  { path: '/register', element: <ClinicSignup />, errorElement: <AppError /> },
  // Share link for a treatment setup: no session, no shell — the token is the whole authorisation.
  { path: '/setup/:token', element: chunk(<SharedSetupPage />), errorElement: <AppError /> },
  {
    path: '/',
    element: <RequireAuth />,
    errorElement: <AppError />,
    children: [
      // Clinic portal: its own shell, deliberately outside the staff AppShell.
      {
        path: 'portal',
        element: <PortalLayout />,
        children: [
          { index: true, element: <PortalHome /> },
          { path: 'cases', element: <PortalCases /> },
          { path: 'submit', element: <SubmitCaseWizard /> },
          { path: 'cases/:id', element: <PortalCaseDetail /> },
          { path: 'invoices', element: <PortalInvoices /> },
          { path: 'invoices/:id/print', element: <PortalInvoicePrintPage /> },
          { path: 'ledger', element: <PortalLedger /> },
          { path: 'pay', element: <PortalPay /> },
          { path: 'notices', element: <PortalNotices /> },
          { path: 'assessment', element: <PortalAssessment /> },
          { path: 'book-scan', element: <PortalBookScan /> },
          { path: 'preferences', element: <PortalPreferences /> },
          { path: 'profile', element: <PortalProfile /> },
          { path: 'chat', element: <PortalChat /> },
        ],
      },
      // Marketing rep portal: a phone app, so it gets its own shell like the clinic portal does.
      {
        path: 'field',
        element: chunk(<FieldLayout />),
        children: [
          { index: true, element: chunk(<FieldToday />) },
          { path: 'duty', element: chunk(<FieldDuty />) },
          { path: 'visit', element: chunk(<FieldVisitForm />) },
          { path: 'day', element: chunk(<FieldMyDay />) },
          { path: 'today', element: <Navigate to="/field" replace /> },
          { path: 'plan', element: chunk(<FieldPlan />) },
          { path: 'clinics', element: chunk(<FieldClinics />) },
          { path: 'followups', element: chunk(<FieldFollowups />) },
        ],
      },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <AudienceHome /> },
          { path: 'chat', element: <ChatPage /> },
          {
            path: 'cases',
            element: <RequirePermission permission="case:read" />,
            children: [
              // Anyone landing on /cases — a bookmark, an old link — wants the live case list.
              { index: true, element: <Navigate to="/workflow/cases" replace /> },
              { path: 'legacy', element: <CasesPage /> },
              { path: ':id', element: <CaseDetailPage /> },
            ],
          },
          {
            path: 'workflow',
            element: <RequirePermission permission="workflow:read" />,
            children: [
              {
                element: <WorkflowLayout />,
                children: [
                  { index: true, element: <WfDashboardPage /> },
                  { path: 'board', element: <WfBoardPage /> },
                  { path: 'cases', element: <WfCasesPage /> },
                  { path: 'cases/:id', element: <WfCaseDetailPage /> },
                  { path: 'calendar', element: <WfCalendarPage /> },
                  { path: 'compliance', element: <WfCompliancePage /> },
                  { path: 'scan', element: chunk(<WfScanPage />) },
                  { path: 'inbox', element: <WfInboxPage /> },
                  {
                    // Ledger work is the accountant's, not the case handler's — guard it with the
                    // finance permission rather than a workflow one.
                    path: 'billing',
                    element: <RequirePermission permission="finance:read" />,
                    children: [{ index: true, element: <WfBillingPage /> }],
                  },
                  {
                    path: 'reports',
                    element: <RequirePermission permission="workflow:reports" />,
                    children: [{ index: true, element: <WfReportsPage /> }],
                  },
                  {
                    path: 'admin',
                    element: <RequirePermission permission="workflow:configure" />,
                    children: [{ index: true, element: <WfAdminPage /> }],
                  },
                ],
              },
            ],
          },
          {
            // The team board is a manager's screen: it needs field:team, which an area manager can
            // hold without being able to watch every rep's live position.
            path: 'tracking/team',
            element: <RequirePermission permission="field:team" />,
            children: [{ index: true, element: chunk(<LabFieldTeam />) }],
          },
          {
            path: 'tracking',
            element: <RequirePermission permission="field:read" />,
            children: [
              { index: true, element: chunk(<LabFieldLive />) },
              { path: 'places', element: chunk(<LabFieldPlaces />) },
              { path: 'reports', element: chunk(<LabFieldReports />) },
              { path: 'territories', element: chunk(<LabFieldTerritories />) },
              { path: 'reps/:id', element: chunk(<LabFieldRepDay />) },
              { path: 'report/print', element: chunk(<FieldReportPrintPage />) },
            ],
          },
          {
            path: 'clinics',
            element: <RequirePermission permission="clinic:read" />,
            children: [
              { index: true, element: <ClinicsPage /> },
              { path: ':id/cases', element: chunk(<ClinicCasesPage />) },
              // The clinic page (cases + ledger). Field reports and older links point at the bare id.
              { path: ':id', element: chunk(<ClinicCasesPage />) },
            ],
          },
          {
            path: 'patients',
            element: <RequirePermission permission="patient:read" />,
            children: [{ index: true, element: <PatientsPage /> }],
          },
          {
            path: 'patients/media',
            element: <RequirePermission permission="patient:review_media" />,
            children: [{ index: true, element: <MediaReviewPage /> }],
          },
          {
            path: 'manufacturing',
            element: <RequirePermission permission="manufacturing:read" />,
            children: [{ index: true, element: <ManufacturingPage /> }],
          },
          {
            path: 'finance/invoices',
            element: <RequirePermission permission="finance:read" />,
            children: [
              { index: true, element: <InvoicesPage /> },
              { path: ':id/print', element: <StaffInvoicePrintPage /> },
            ],
          },
          {
            // The general ledger. Read is the gate to the section; posting and closing are checked
            // per action inside it, so an accountant and a manager can share the same screens.
            path: 'accounting',
            element: <RequirePermission permission="accounting:read" />,
            children: [
              {
                element: chunk(<AccountingLayout />),
                children: [
                  { index: true, element: chunk(<AccountingOverview />) },
                  { path: 'journals', element: chunk(<JournalsPage />) },
                  { path: 'journals/new', element: chunk(<NewJournalPage />) },
                  { path: 'expenses/new', element: chunk(<NewExpensePage />) },
                  { path: 'opening-balances', element: chunk(<OpeningBalancesPage />) },
                  { path: 'trial-balance', element: chunk(<TrialBalancePage />) },
                  { path: 'profit-loss', element: chunk(<ProfitLossPage />) },
                  { path: 'balance-sheet', element: chunk(<BalanceSheetPage />) },
                  { path: 'accounts', element: chunk(<ChartOfAccountsPage />) },
                  { path: 'accounts/:id', element: chunk(<AccountLedgerPage />) },
                  { path: 'periods', element: chunk(<PeriodsPage />) },
                ],
              },
            ],
          },
          {
            path: 'finance/payments',
            element: <RequirePermission permission="finance:read" />,
            children: [{ index: true, element: <PaymentsPage /> }],
          },
          {
            // The old expenses screen wrote to a table the general ledger never read, so anything
            // recorded there was invisible to the books. Money out now has one home.
            path: 'finance/expenses',
            element: <Navigate to="/accounting/expenses/new" replace />,
          },
          {
            path: 'finance/prices',
            element: <RequirePermission permission="finance:read" />,
            children: [{ index: true, element: <PriceListPage /> }],
          },
          {
            path: 'admin/users',
            element: <RequirePermission permission="user:manage" />,
            children: [{ index: true, element: <UsersPage /> }],
          },
          {
            path: 'admin/settings',
            element: <RequirePermission permission="settings:manage" />,
            children: [{ index: true, element: <SettingsPage /> }],
          },
          {
            path: 'admin/notifications',
            element: <RequirePermission permission="notification:read" />,
            children: [{ index: true, element: <NotificationsPage /> }],
          },
          {
            path: 'admin/rbac',
            element: <RequirePermission permission="rbac:manage" />,
            children: [{ index: true, element: <RbacMatrix /> }],
          },
          {
            path: 'leads',
            element: <RequirePermission permission="clinic:manage" />,
            children: [{ index: true, element: <LeadsPage /> }],
          },
          {
            path: 'admin/audit',
            element: <RequirePermission permission="audit:read" />,
            children: [{ index: true, element: <AuditLog /> }],
          },
          {
            path: 'admin/security',
            element: <RequirePermission permission="security:read" />,
            children: [{ index: true, element: <SecurityDashboard /> }],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound />, errorElement: <AppError /> },
]);

export function App() {
  return (
    <>
      <TableCards />
      <RouterProvider router={router} />
    </>
  );
}
